/**
 * Cross-App Sync for OPMODE <-> GunfighterOS Integration
 *
 * Handles activity tracking and bonus calculations between apps.
 * Uses the bridge architecture for data exchange.
 *
 * NOTE: Account linking is handled by gunfighterBridge.ts
 */

import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db as firestore } from './firebase';
import { loadProgressionState, saveProgressionState } from './progressionService';
import { getWeekKey } from './xpSystem';
import {
  isGunfighterLinked,
  publishOpmodeSnapshot,
  syncGunfighterBridge,
  getGunfighterBridgeStatus,
  type BridgeStatus,
} from './gunfighterBridge';

// Re-export bridge functions for backwards compatibility
export {
  isGunfighterLinked,
  publishOpmodeSnapshot,
  syncGunfighterBridge,
  getGunfighterBridgeStatus,
  type BridgeStatus,
} from './gunfighterBridge';

export {
  linkGunfighterAccount,
  unlinkGunfighterAccount,
} from './gunfighterBridge';

// ============================================================================
// TYPES
// ============================================================================

export interface CrossAppActivity {
  odexieId: string;
  opmodeUserId: string;
  gunfighterUserId: string;
  linkedAt: string;

  // Weekly activity tracking
  lastOpmodeSession: string;     // ISO date
  lastGunfighterSession: string;
  currentWeekOpmodeCount: number;
  currentWeekGunfighterCount: number;
  currentWeekKey: string;        // ISO week key for reset detection

  // Streak tracking
  consecutiveDualWeeks: number;
  totalDualWeeks: number;

  // Bonus flags
  linkBonusClaimed: boolean;
  dualTrainingBonusClaimedWeek: string; // Week key when last claimed
  consistencyBonusesClaimed: string[];  // Milestone IDs
}

// ============================================================================
// ACTIVITY TRACKING
// ============================================================================

/**
 * Get or create cross-app activity record
 */
async function getOrCreateActivity(userId: string): Promise<CrossAppActivity | null> {
  const isLinked = await isGunfighterLinked(userId);
  if (!isLinked) {
    return null;
  }

  const state = await loadProgressionState(userId);
  const gunfighterUserId = state.gunfighterUserId!;

  const activityRef = doc(firestore, 'crossAppActivity', userId);
  const activityDoc = await getDoc(activityRef);

  if (activityDoc.exists()) {
    return activityDoc.data() as CrossAppActivity;
  }

  // Create new activity record
  const newActivity: CrossAppActivity = {
    odexieId: userId,
    opmodeUserId: userId,
    gunfighterUserId,
    linkedAt: state.gunfighterLinkedAt || new Date().toISOString(),
    lastOpmodeSession: '',
    lastGunfighterSession: '',
    currentWeekOpmodeCount: 0,
    currentWeekGunfighterCount: 0,
    currentWeekKey: getWeekKey(),
    consecutiveDualWeeks: 0,
    totalDualWeeks: 0,
    linkBonusClaimed: false,
    dualTrainingBonusClaimedWeek: '',
    consistencyBonusesClaimed: [],
  };

  await setDoc(activityRef, newActivity);
  return newActivity;
}

/**
 * Record an OPMODE session for cross-app bonus tracking
 * Called after session completion
 */
export async function recordOpmodeSession(userId: string, sessionDate: Date = new Date()): Promise<void> {
  const activity = await getOrCreateActivity(userId);
  if (!activity) {
    return; // Not linked, no tracking needed
  }

  const currentWeek = getWeekKey(sessionDate);
  const dateStr = sessionDate.toISOString().split('T')[0];

  // Check if week changed and reset counters
  const needsWeekReset = activity.currentWeekKey !== currentWeek;

  const updates: Partial<CrossAppActivity> = {
    lastOpmodeSession: dateStr,
    currentWeekOpmodeCount: needsWeekReset ? 1 : (activity.currentWeekOpmodeCount + 1),
    currentWeekKey: currentWeek,
  };

  if (needsWeekReset) {
    // Check if previous week was a dual training week
    if (
      activity.currentWeekOpmodeCount > 0 &&
      activity.currentWeekGunfighterCount > 0
    ) {
      updates.consecutiveDualWeeks = (activity.consecutiveDualWeeks || 0) + 1;
      updates.totalDualWeeks = (activity.totalDualWeeks || 0) + 1;
    } else if (activity.consecutiveDualWeeks > 0) {
      // Streak broken
      updates.consecutiveDualWeeks = 0;
    }

    // Reset GunfighterOS count for new week
    updates.currentWeekGunfighterCount = 0;
  }

  await updateDoc(doc(firestore, 'crossAppActivity', userId), updates);

  // Update last sync timestamp in progression state
  const state = await loadProgressionState(userId);
  state.gunfighterLastSync = new Date().toISOString();
  await saveProgressionState(state);

  // Publish OPMODE snapshot for GunfighterOS
  await publishOpmodeSnapshot(userId);
}

/**
 * Get current activity for a user
 */
export async function getCrossAppActivity(userId: string): Promise<CrossAppActivity | null> {
  return getOrCreateActivity(userId);
}

/**
 * Check if dual training bonus is eligible this week
 */
export async function isDualTrainingBonusEligible(userId: string): Promise<boolean> {
  const activity = await getOrCreateActivity(userId);
  if (!activity) {
    return false;
  }

  const currentWeek = getWeekKey();

  return (
    activity.currentWeekOpmodeCount > 0 &&
    activity.currentWeekGunfighterCount > 0 &&
    activity.dualTrainingBonusClaimedWeek !== currentWeek
  );
}

/**
 * Claim the dual training bonus for this week
 */
export async function claimDualTrainingBonus(userId: string): Promise<boolean> {
  const eligible = await isDualTrainingBonusEligible(userId);
  if (!eligible) {
    return false;
  }

  await updateDoc(doc(firestore, 'crossAppActivity', userId), {
    dualTrainingBonusClaimedWeek: getWeekKey(),
  });

  return true;
}

// ============================================================================
// LINK BONUS TRACKING
// ============================================================================

/**
 * Check if link bonus has been claimed
 */
export async function isLinkBonusClaimed(userId: string): Promise<boolean> {
  const activity = await getOrCreateActivity(userId);
  return activity?.linkBonusClaimed ?? false;
}

/**
 * Claim the one-time link bonus
 */
export async function claimLinkBonus(userId: string): Promise<boolean> {
  const activity = await getOrCreateActivity(userId);
  if (!activity || activity.linkBonusClaimed) {
    return false;
  }

  await updateDoc(doc(firestore, 'crossAppActivity', userId), {
    linkBonusClaimed: true,
  });

  return true;
}

// ============================================================================
// CONSISTENCY MILESTONE TRACKING
// ============================================================================

/**
 * Get claimed consistency milestones
 */
export async function getClaimedMilestones(userId: string): Promise<string[]> {
  const activity = await getOrCreateActivity(userId);
  return activity?.consistencyBonusesClaimed ?? [];
}

/**
 * Claim a consistency milestone
 */
export async function claimConsistencyMilestone(userId: string, milestoneId: string): Promise<boolean> {
  const activity = await getOrCreateActivity(userId);
  if (!activity) {
    return false;
  }

  if (activity.consistencyBonusesClaimed.includes(milestoneId)) {
    return false; // Already claimed
  }

  const newClaimed = [...activity.consistencyBonusesClaimed, milestoneId];
  await updateDoc(doc(firestore, 'crossAppActivity', userId), {
    consistencyBonusesClaimed: newClaimed,
  });

  return true;
}

// ============================================================================
// LEGACY EXPORTS (for backwards compatibility)
// ============================================================================

// These are kept for any code that might still reference the old API
export async function getLinkStatus(userId: string): Promise<{
  isLinked: boolean;
  link: { linkBonusClaimed: boolean } | null;
  activity: CrossAppActivity | null;
}> {
  const isLinked = await isGunfighterLinked(userId);

  if (!isLinked) {
    return { isLinked: false, link: null, activity: null };
  }

  const activity = await getCrossAppActivity(userId);

  return {
    isLinked: true,
    link: activity ? { linkBonusClaimed: activity.linkBonusClaimed } : null,
    activity,
  };
}
